import * as React from "react"
import {useMemo, useState} from "react"
import {ResizableHandle, ResizablePanel, ResizablePanelGroup} from "@/components/ui/resizable";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {TooltipProvider} from "@/components/ui/tooltip";
import {OperatorOutlet, OperatorOutletHistory, WorkflowLog} from "@/model/Log";
import {OperatorOutletDetail} from "@/view/workflowLog/OperatorOutletDetail";
import classNames from "classnames";
import WorkflowGraph from "@/view/workflow/components/workflowGraph/WorkflowGraph";
import useWorkflow from "@/query/workflow/useWorkflow";
import moment from "moment";
import OperatorForm from "@/view/workflow/components/operatorForm/OperatorForm";
import OperatorIcon from "@/view/workflow/components/OperatorIcon";
import {InputOperator, Operator} from "@/model/Workflow";
import Editor from "@monaco-editor/react";
import {type OperatorNode} from "@/view/workflow/components/workflowGraph/types";

interface WorkflowLogViewerProps {
    className?: string
    log: WorkflowLog
    defaultLayout: number[] | undefined
}

const toOperatorOutletArray = (outlet: OperatorOutletHistory): OperatorOutlet[] => {
    return Array.isArray(outlet) ? outlet : [outlet];
}

const getOutletDuration = (outlet: OperatorOutlet): number => {
    return moment.utc(outlet.endedAt).diff(moment.utc(outlet.startedAt), 'milliseconds');
}

const getOutletAverageDuration = (outlet: OperatorOutletHistory): number | undefined => {
    const outlets = toOperatorOutletArray(outlet);
    if (outlets.length === 0)
        return undefined;

    const totalDuration = outlets.reduce((sum, outletItem) => sum + getOutletDuration(outletItem), 0);
    return Math.round(totalDuration / outlets.length);
}

const WorkflowLogViewer: React.FC<WorkflowLogViewerProps> = (props) => {
    const {
        className,
        log,
        defaultLayout = [52, 48]
    } = props;
    const {data: workflow} = useWorkflow(log.workflowId);

    const [selectedOperatorId, setSelectedOperatorId] = useState<string>(log.resultOperatorId);

    const focusedInfo: undefined | { type: "input", params: any } | {
        type: "operator",
        operator: Operator,
        outlet?: OperatorOutletHistory
    } = useMemo(() => {
        if (!workflow || !selectedOperatorId)
            return undefined;

        if (selectedOperatorId === 'input')
            return {
                type: "input",
                params: log.params
            };

        return {
            type: "operator",
            operator: workflow.operators[selectedOperatorId],
            outlet: (selectedOperatorId in log.operatorOutlets) ? log.operatorOutlets[selectedOperatorId] : undefined
        }
    }, [log.operatorOutlets, log.params, selectedOperatorId, workflow])

    const failedOperatorIds = useMemo(() => {
        return Object.entries(log.operatorOutlets)
            .filter((entry) => {
                if (!Array.isArray(entry[1]))
                    return entry[1].type === 'failed';
                return entry[1].some(operatorOutlet => operatorOutlet.type === 'failed');
            })
            .map((entry) => entry[0])
    }, [log.operatorOutlets])


    const operators = useMemo(() => {
        if (!workflow)
            return null;

        const result = JSON.parse(JSON.stringify(workflow.operators))
        result['input'] = {
            _id: "input",
            type: 'input',
            parameter: log.params,
            nextOperatorId: workflow.firstOperatorId
        } as InputOperator

        return result
    }, [log.params, workflow])

    const visitedNodeIds = useMemo(() => {
        return ['input', ...Object.keys(log.operatorOutlets)]
    }, [log.operatorOutlets])

    const operatorDurations = useMemo(() => {
        if (!workflow)
            return undefined;

        const result: { [key: string]: number } = {}
        Object.entries(log.operatorOutlets).forEach(([operatorId, outlet]) => {
            const duration = getOutletAverageDuration(outlet);
            if (duration !== undefined)
                result[operatorId] = duration;
        })

        return result
    }, [log.operatorOutlets, workflow])

    const operatorExecutionOrders = useMemo(() => {
        const result: { [key: string]: number[] } = {}

        const entries = Object.entries(log.operatorOutlets).flatMap(([operatorId, outletOrOutlets]) => {
            return toOperatorOutletArray(outletOrOutlets)
                .map((outlet) => ({
                    operatorId,
                    outlet,
                }));
        })

        entries
            .sort((a, b) => {
                // @ts-ignore
                const startedAtA = moment.utc(a.outlet.startedAt).valueOf();
                // @ts-ignore
                const startedAtB = moment.utc(b.outlet.startedAt).valueOf();

                if (startedAtA !== startedAtB)
                    return startedAtA - startedAtB;
                return a.operatorId.localeCompare(b.operatorId);
            })
            .forEach(({operatorId}, index) => {
                result[operatorId] = [...(result[operatorId] ?? []), index + 1];
            })

        return result;
    }, [log.operatorOutlets])


    const rightPanel = useMemo(() => {
        if (!focusedInfo || !workflow) return undefined

        if (focusedInfo.type === 'input') {
            return (
                <Editor className='grow bg-black'
                        defaultLanguage="json"
                        options={{readOnly: true}}
                        defaultValue={JSON.stringify(focusedInfo.params, null, 2)}
                        theme="dark"
                />
            )
        }

        if (focusedInfo.outlet) {
            return (
                <Tabs defaultValue="log" className="flex flex-col py-2 px-3 h-full overflow-hidden">
                    <TabsList className="grid w-full grid-cols-2 shrink-0 grow-0 basis-[20px]">
                        <TabsTrigger value="log">Log</TabsTrigger>
                        <TabsTrigger value="operator">Operator</TabsTrigger>
                    </TabsList>
                    <TabsContent value="log" className='grow overflow-y-auto'>
                        {!Array.isArray(focusedInfo.outlet) && focusedInfo.outlet && (
                            <OperatorOutletDetail operatorOutlet={focusedInfo.outlet}
                                                  operator={focusedInfo.operator}/>
                        )}
                        {Array.isArray(focusedInfo.outlet) && focusedInfo.outlet.length > 0 && (
                            <Tabs defaultValue={`log_${Math.max(focusedInfo.outlet.length - 1, 0)}`} className="flex h-full flex-col">
                                <TabsList className="shrink-0">
                                    {focusedInfo.outlet.map((outletItem, idx) => (
                                        <TabsTrigger key={`log-trigger-${idx}`} value={`log_${idx}`}>
                                            <span
                                                className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs font-semibold leading-none text-blue-700 shadow-sm"
                                            >
                                                {operatorExecutionOrders[selectedOperatorId]?.[idx] ?? idx + 1}
                                            </span>
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                                {focusedInfo.outlet.map((outletItem, idx) => (
                                    <TabsContent key={`log-content-${idx}`} value={`log_${idx}`} className="grow overflow-y-auto">
                                        <OperatorOutletDetail operatorOutlet={outletItem}
                                                              operator={focusedInfo.operator}
                                                              duration={getOutletDuration(outletItem)}/>
                                    </TabsContent>
                                ))}
                            </Tabs>
                        )}
                    </TabsContent>
                    <TabsContent value="operator" className='grow overflow-y-auto'>
                        <OperatorForm workflow={workflow}
                                      operator={(focusedInfo.operator as Operator)}
                                      readOnly={true}
                        />
                    </TabsContent>
                </Tabs>
            )
        }

        return (
            <OperatorForm workflow={workflow}
                          operator={(focusedInfo.operator as Operator)}
                          readOnly={true}
            />
        )
    }, [focusedInfo, operatorExecutionOrders, selectedOperatorId, workflow])

    const onClickNode = (node: OperatorNode) => {
        setSelectedOperatorId(node.id);
    }

    if (!workflow || !operators)
        return null;

    return (
        <TooltipProvider delayDuration={0}>
            <ResizablePanelGroup
                direction="horizontal"
                onLayout={(sizes: number[]) => {
                    localStorage.setItem("react-resizable-panels:layout:mail", JSON.stringify(
                        sizes
                    ))
                }}
                className={classNames("h-full items-stretch flex flex-col", className)}
            >
                <ResizablePanel defaultSize={defaultLayout[0]} minSize={30}>
                    <WorkflowGraph operators={operators}
                                   editable={false}
                                   onClickNode={onClickNode}
                                   focusedNodeId={selectedOperatorId}
                                   visitedNodeIds={visitedNodeIds}
                                   failedNodeIds={failedOperatorIds}
                                   operatorDurations={operatorDurations}
                                   operatorExecutionOrders={operatorExecutionOrders}
                    />
                </ResizablePanel>

                {rightPanel && focusedInfo &&
                    <>
                        <ResizableHandle withHandle/>
                        <ResizablePanel defaultSize={defaultLayout[1]} minSize={30} collapsible={true}
                                        className='h-full flex flex-col p-3'
                        >
                            <div className="gap-4 text-sm mb-4 px-2">
                                <div className="grid gap-1">
                                    <div className='flex items-center'>
                                        <OperatorIcon
                                            type={focusedInfo.type === 'input' ? 'input' : focusedInfo.operator.type}
                                            size={30}/>
                                        <h3
                                            className="font-semibold grow ml-2">{focusedInfo.type === 'input' ? 'input' : focusedInfo.operator.type}
                                        </h3>
                                        {focusedInfo.type === 'operator' && focusedInfo.outlet && (() => {
                                            const duration = getOutletAverageDuration(focusedInfo.outlet)
                                            if (duration === undefined) return null

                                            return (
                                                <span className='text-xs'>
                                                    {duration.toLocaleString("ko-KR")}ms
                                                </span>
                                            )
                                        })()}
                                    </div>
                                </div>
                            </div>
                            {rightPanel}

                        </ResizablePanel>
                    </>
                }

            </ResizablePanelGroup>
        </TooltipProvider>
    )
}

export default WorkflowLogViewer
